import { Fragment, useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Button, Container, Input } from "reactstrap";
import { getNotifications, markNotificationAsRead, markAllNotificationsAsRead } from "../../utils/apiCalls";
import { C, tbl, reportingGlobalCSS } from "../reporting/reportingStyles";
import { RCard, Badge, Loader } from "../reporting/ReportingShared";
import Breadcrumb from "../common/breadcrumb";

const typeLabels = {
  reservation_pending_reminder: "Pending Reminder",
  reservation_no_table: "No Table",
  reservation_arriving: "Arriving Soon",
  reservation_no_show: "No-Show",
  waitlist: "Waitlist",
  order: "Order",
  stuck_order: "Stuck Order",
  pos_device_stale_orders: "Stale Tablet Orders",
  system: "System",
};

const typeBadge = (t) => {
  const map = {
    reservation_pending_reminder: { color: C.orange, bg: C.orangeLight },
    reservation_no_table: { color: C.coral, bg: C.coralLight },
    reservation_arriving: { color: C.blue, bg: C.blueLight },
    reservation_no_show: { color: C.coral, bg: C.coralLight },
    waitlist: { color: C.green, bg: C.greenLight },
    order: { color: C.purple, bg: C.purpleLight },
    stuck_order: { color: C.coral, bg: C.coralLight },
    pos_device_stale_orders: { color: C.orange, bg: C.orangeLight },
    system: { color: C.textMed, bg: C.borderLight },
  };
  return map[t] || { color: C.textMed, bg: C.borderLight };
};

// Kept in step with the same map in NotificationBell.js — a notification that navigates from
// the bell but does nothing on this page is the more confusing of the two failures.
const referenceRoutes = {
  reservation: (id) => `/reservations/edit/${id}`,
  waitlist: (id) => `/table-management/edit-waitlist/${id}`,
  stuck_order_ticket: () => `/Orders/StuckOrders`,
  pos_device: () => `/devices/devices-list`,
};

const timeAgo = (dateStr) => {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};

const NotificationList = () => {
  const navigate = useNavigate();
  const userType = localStorage.getItem("userType");
  const merchantId = localStorage.getItem("MerchantId");
  const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
  const selectedMerchant = userType === "merchant" ? merchantId : reduxSelectedMerchant;

  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [filterType, setFilterType] = useState("");
  const [filterRead, setFilterRead] = useState("");

  const fetchData = useCallback(async () => {
    if (!selectedMerchant || selectedMerchant === "all") { setNotifications([]); return; }
    setIsLoading(true);
    const params = { MerchantId: selectedMerchant, page: currentPage, limit: 20 };
    if (filterType) params.type = filterType;
    if (filterRead) params.isRead = filterRead;
    const data = await getNotifications(params);
    setNotifications(data?.rows || []);
    setTotalPages(data?.totalPages || 0);
    setTotal(data?.total || 0);
    setIsLoading(false);
  }, [selectedMerchant, currentPage, filterType, filterRead]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setCurrentPage(1); }, [filterType, filterRead]);

  const handleClick = async (n) => {
    if (!n.isRead) {
      await markNotificationAsRead(n.id);
      setNotifications((prev) => prev.map((item) => item.id === n.id ? { ...item, isRead: true } : item));
    }
    // Gated on the ROUTE existing, not on referenceId: a merchant-level notification (several
    // tablets, so no single row to link to) carries a null referenceId, and the old check meant
    // clicking it did nothing at all.
    if (n.referenceType) {
      const getRoute = referenceRoutes[n.referenceType];
      if (getRoute) navigate(getRoute(n.referenceId));
    }
  };

  const handleMarkAllRead = async () => {
    if (!selectedMerchant) return;
    const result = await markAllNotificationsAsRead(selectedMerchant);
    if (result) setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
  };

  return (
    <Fragment>
      <style>{reportingGlobalCSS}</style>
      <Breadcrumb title="All Notifications" parent="Notifications" />
      <Container fluid className="rpt-wrap">
        {/* Filters */}
        <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 20, flexWrap: "wrap" }}>
          <Input type="select" value={filterType} onChange={(e) => setFilterType(e.target.value)} style={{ maxWidth: 200, borderRadius: 8, border: `1px solid ${C.border}` }}>
            <option value="">All Types</option>
            {Object.entries(typeLabels).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </Input>
          <Input type="select" value={filterRead} onChange={(e) => setFilterRead(e.target.value)} style={{ maxWidth: 140, borderRadius: 8, border: `1px solid ${C.border}` }}>
            <option value="">All</option>
            <option value="false">Unread</option>
            <option value="true">Read</option>
          </Input>
          <div style={{ flex: 1 }} />
          <Button size="sm" style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 8, color: C.textMed, fontWeight: 600, fontSize: 12 }} onClick={handleMarkAllRead}>
            Mark All as Read
          </Button>
        </div>

        {/* Table */}
        <RCard style={{ padding: 0, overflow: "hidden" }}>
          {isLoading ? <Loader /> : (!selectedMerchant || selectedMerchant === "all") ? (
            <p style={{ textAlign: "center", padding: 40, color: C.textLight }}>Select a merchant</p>
          ) : notifications.length === 0 ? (
            <p style={{ textAlign: "center", padding: 40, color: C.textLight }}>No notifications</p>
          ) : (
            <>
              <div style={{ overflowX: "auto" }}>
                <table style={tbl.table}>
                  <thead>
                    <tr>
                      <th style={{ ...tbl.th, width: 8 }}></th>
                      {["Type", "Title", "Message", "Branch", "Time", ""].map((h) => <th key={h} style={tbl.th}>{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {notifications.map((n) => {
                      const tb = typeBadge(n.type);
                      return (
                        <tr
                          key={n.id}
                          className="rpt-row"
                          style={{ background: n.isRead ? C.white : "#F0F7FF", cursor: n.referenceType ? "pointer" : "default" }}
                          onClick={() => handleClick(n)}
                        >
                          <td style={tbl.td}>
                            {!n.isRead && <div style={{ width: 8, height: 8, borderRadius: 4, background: C.blue }} />}
                          </td>
                          <td style={tbl.td}><Badge color={tb.color} bg={tb.bg}>{typeLabels[n.type] || n.type}</Badge></td>
                          <td style={{ ...tbl.td, fontWeight: n.isRead ? 400 : 700, color: C.text }}>{n.title}</td>
                          <td style={{ ...tbl.td, color: C.textMed, maxWidth: 300 }}>{n.message}</td>
                          <td style={tbl.td}>{n.Branch?.name || "—"}</td>
                          <td style={{ ...tbl.td, whiteSpace: "nowrap", color: C.textLight, fontSize: 12 }}>{timeAgo(n.createdAt)}</td>
                          <td style={tbl.td}>
                            {n.referenceType && (
                              <Button size="sm" style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 6, color: C.blue, fontSize: 11, fontWeight: 700 }} onClick={(e) => { e.stopPropagation(); handleClick(n); }}>View</Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", borderTop: `1px solid ${C.borderLight}` }}>
                  <span style={{ fontSize: 12, color: C.textLight }}>Showing {(currentPage - 1) * 20 + 1}–{Math.min(currentPage * 20, total)} of {total}</span>
                  <div style={{ display: "flex", gap: 6 }}>
                    <Button size="sm" style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 6, color: C.textMed, fontWeight: 600, fontSize: 12 }} disabled={currentPage === 1} onClick={() => setCurrentPage((p) => p - 1)}>Previous</Button>
                    <span style={{ padding: "6px 12px", fontSize: 12, color: C.textMed }}>{currentPage} / {totalPages}</span>
                    <Button size="sm" style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 6, color: C.textMed, fontWeight: 600, fontSize: 12 }} disabled={currentPage === totalPages} onClick={() => setCurrentPage((p) => p + 1)}>Next</Button>
                  </div>
                </div>
              )}
            </>
          )}
        </RCard>
      </Container>
    </Fragment>
  );
};

export default NotificationList;
